import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import {
  preferredWeekdayFromDate,
  subscriptionFrequencySupportsPreferredWeekday,
  subscriptionPreferredWeekdayOptions,
} from '@/lib/subscription-frequency'
import { Repeat2 } from 'lucide-react'

const subscriptionScheduleCadenceLabels = {
  monthly: 'Each month',
  every_3_months: 'Every 3 months',
  every_6_months: 'Every 6 months',
  yearly: 'Each year',
}

function ordinalDay(dateYmd) {
  const day = Number(String(dateYmd || '').slice(-2))

  if (!Number.isInteger(day) || day < 1 || day > 31) {
    return ''
  }

  const remainder = day % 100
  if (remainder >= 11 && remainder <= 13) {
    return `${day}th`
  }

  return `${day}${{ 1: 'st', 2: 'nd', 3: 'rd' }[day % 10] || 'th'}`
}

function topLevelSubscriptionItems(cart) {
  return (cart?.cart_items ?? []).filter(
    (item) =>
      !item?.parent_cart_item_id &&
      subscriptionFrequencySupportsPreferredWeekday(item?.subscription_frequency)
  )
}

export function getPosSubscriptionScheduleDefaults(cart) {
  const subscriptionItems = topLevelSubscriptionItems(cart)
  const frequencies = [
    ...new Set(subscriptionItems.map((item) => item.subscription_frequency).filter(Boolean)),
  ]

  return {
    hasPreferredWeekdaySchedule: subscriptionItems.length > 0,
    preferredWeekday:
      subscriptionItems.length > 0
        ? subscriptionItems.find((item) => item.preferred_fulfillment_weekday)
            ?.preferred_fulfillment_weekday ||
          preferredWeekdayFromDate(String(cart?.fulfillment_due_date || '').slice(0, 10))
        : '',
    cadenceLabel:
      frequencies.length === 1 ? subscriptionScheduleCadenceLabels[frequencies[0]] || null : null,
  }
}

function scheduleDescription({ cadenceLabel, date, fulfillmentType, preferredWeekday }) {
  const fulfillmentLabel = fulfillmentType === 'delivery' ? 'delivery' : 'pickup'
  const preferredWeekdayLabel =
    subscriptionPreferredWeekdayOptions.find((option) => option.id === preferredWeekday)?.name ||
    'selected weekday'
  const dateLabel = ordinalDay(date)

  if (!cadenceLabel) {
    return `Your month-based ${fulfillmentLabel} orders will follow their selected frequency and be scheduled for the ${preferredWeekdayLabel} closest to the ${dateLabel}.`
  }

  return `${cadenceLabel}, your ${fulfillmentLabel} will be scheduled for the ${preferredWeekdayLabel} closest to the ${dateLabel}.`
}

export function PosSubscriptionScheduleField({ form, cart, dateFieldName, fulfillmentType }) {
  const scheduleDefaults = getPosSubscriptionScheduleDefaults(cart)

  if (!scheduleDefaults.hasPreferredWeekdaySchedule) {
    return null
  }

  return (
    <form.Subscribe
      selector={(state) => ({
        date: state.values[dateFieldName],
        preferredWeekday: state.values.preferredWeekday,
      })}
    >
      {({ date, preferredWeekday }) =>
        date ? (
          <Alert variant="brand">
            <Repeat2 className="size-4" />
            <AlertTitle>
              Set your {fulfillmentType === 'delivery' ? 'delivery' : 'pickup'} schedule
            </AlertTitle>
            <AlertDescription className="space-y-3">
              <p>
                {scheduleDescription({
                  cadenceLabel: scheduleDefaults.cadenceLabel,
                  date,
                  fulfillmentType,
                  preferredWeekday: preferredWeekday || preferredWeekdayFromDate(date),
                })}
              </p>
              <form.AppField name="preferredWeekday">
                {(field) => (
                  <field.SelectInput
                    id={`pos-subscription-${fulfillmentType}-preferred-weekday`}
                    label="Preferred weekday"
                    options={subscriptionPreferredWeekdayOptions}
                    placeholder="Choose weekday"
                    className="max-w-sm"
                  />
                )}
              </form.AppField>
            </AlertDescription>
          </Alert>
        ) : null
      }
    </form.Subscribe>
  )
}
